import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';

import { appT } from '~/composables/useAppI18n';

type TaskFilterItem = { key: string; label?: string };

type TranslateFn = (key: string) => string;

type CodeOption = { label: string; value: string };

/** UI 고정 필터 — 평가는 서버 label 그대로, 진행상태(result-*)만 코드 i18n */
const SUB_FILTER_I18N_KEYS: Record<string, string> = {
  'status-all': 'task.home.subFilter.all',
  'status-owner': 'task.home.subFilter.myTask',
  'status-coop': 'task.home.subFilter.coopTask',
  'status-imp': 'task.home.subFilter.importantTask',
  'status-delay': 'task.home.subFilter.delayedTask',
  'result-all': 'task.home.subFilter.all',
};

export const translateResultStatusLabelWith = (codeId: string | undefined, t: TranslateFn, fallback?: string) => {
  const code = String(codeId ?? '');
  if (!code) return fallback ?? '';
  const i18nKey = `task.common.resultStatus.${code}`;
  const translated = t(i18nKey);
  if (translated !== i18nKey) return translated;
  return fallback ?? '';
};

export const translateResultStatusLabel = (codeId?: string, fallback?: string) =>
  translateResultStatusLabelWith(codeId, appT, fallback);

const translateTaskFilterLabelWith = (filter: TaskFilterItem, t: TranslateFn) => {
  const fixedKey = SUB_FILTER_I18N_KEYS[filter.key];
  if (fixedKey) {
    return t(fixedKey);
  }

  if (filter.key.startsWith('result-')) {
    const code = filter.key.slice('result-'.length);
    if (code && code !== 'all') {
      return translateResultStatusLabelWith(code, t, filter.label);
    }
  }

  return filter.label ?? '';
};

/** store·비동기 handler에서 필터 라벨 번역 */
export const translateTaskFilterLabel = (filter: TaskFilterItem) =>
  translateTaskFilterLabelWith(filter, appT);

/** Vue 컴포넌트 — locale 변경 시 라벨 재계산 */
export const useTaskFilterI18n = () => {
  const { locale, t } = useI18n();

  const getFilterLabel = (filter: TaskFilterItem) => {
    locale.value;
    return translateTaskFilterLabelWith(filter, t);
  };

  const localizeFilters = (filters: TaskFilterItem[] = []) =>
    computed(() => {
      locale.value;
      return filters.map(filter => ({
        ...filter,
        label: translateTaskFilterLabelWith(filter, t),
      }));
    });

  const localizeResultStatusOptions = (options: MaybeRefOrGetter<CodeOption[]>) =>
    computed(() => {
      locale.value;
      return (toValue(options) || []).map(opt => ({
        ...opt,
        label: translateResultStatusLabelWith(String(opt.value), t, opt.label),
      }));
    });

  return { getFilterLabel, localizeFilters, localizeResultStatusOptions };
};

/** 결과 상태 필터 점 색상 — 라벨 대신 key 기준 */
export const getResultStatusDotColor = (filterKey: string) => {
  switch (filterKey) {
    case 'result-001':
      return '#3b82f6';
    case 'result-003':
      return '#22c55e';
    case 'result-005':
      return '#f59e0b';
    case 'result-004':
      return '#ef4444';
    default:
      return '#cbd5e1';
  }
};
